import { useRouter } from 'next/router';
import { useState } from 'react';
import Button from '@/atoms/buttons/Button';
import Heading from '@/atoms/typography/Heading';
import Paragraph from '@/atoms/typography/Paragraph';
import TextField from '@/molecules/formElements/TextField/TextField';
import Dialog from '@/molecules/modals/Dialog/Dialog';
import Size from '@enums/Size';
import useCreateAccount from '@pkg/entities/organisations/mutations/useCreateAccount';
import useNotifyAccountCreation from '@pkg/entities/users/useNotifyAccountCreation';
import Box from '@/components/Box';

const CreateAccount = ({ ownerId, open, onClose }) => {
  const create = useCreateAccount();
  const router = useRouter();

  // state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountName, setAccountName] = useState('');
  const [tenantId, setTenantId] = useState(null);

  // Provisioning runs in a queued job; we wait for the broadcast before
  // redirecting (on success) or surfacing an error (on permanent failure).
  useNotifyAccountCreation(ownerId, {
    tenantId,
    onCreated: (data) => {
      router.push(`/a/${data.tenant_id}?context=new-account`);
    },
    onFailed: () => {
      setLoading(false);
      setError(
        "We couldn't finish creating your account. Please try again, or contact support if the problem continues."
      );
    },
  });

  // handlers
  function handleClose() {
    setLoading(false);
    setError(null);
    onClose();
  }

  async function handleCreate() {
    const name = accountName.trim();
    if (!name) {
      setError('Please enter an account name.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await create({ name, user_id: ownerId });
      // Keep the spinner running — the redirect happens once the
      // `.account.created` broadcast arrives for this tenant.
      setTenantId(result.data.createNewAccount.tenant_id);
    } catch {
      setLoading(false);
      setError('Something went wrong starting your account. Please try again.');
    }
  }

  // render
  return (
    <Dialog open={open}>
      <Box pt={3}>
        <Heading variant="h2">Create a new Beamible account.</Heading>
        <Paragraph sx={{ mt: 1, mb: 2 }}>
          IMPORTANT: Please note creating a new Beamible account is a commercial
          transaction. Contact sales@beamible.com for more information.
        </Paragraph>
        <Box>
          <TextField
            name="accountName"
            label="Account name"
            placeholder="Enter a name for the new account"
            debounce={0}
            onChange={(e, value) => setAccountName(value)}
            fullWidth
            size={Size.LARGE}
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </Box>
        {loading && (
          <Paragraph sx={{ mt: 2 }}>Setting up your account…</Paragraph>
        )}
        {error && (
          <Paragraph sx={{ mt: 2, color: 'error.main' }}>{error}</Paragraph>
        )}
        <Box textAlign="right" mt={2}>
          <Button
            boldText
            onClick={handleClose}
            color="primary"
            variant="naked"
            label="Cancel"
            sx={{ mr: 1 }}
            disabled={loading}
          />
          <Button
            boldText
            color="secondary"
            label="Create"
            loading={loading}
            onClick={handleCreate}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateAccount;
